import { useCallback } from 'react';
import { useMap } from 'ahooks';
import type { ISheet } from '../components/ExcelSheetTabs';
import type { IColumnConfig, TTableRow } from '../components/SmartTableGrid';
import { generateBlankSheetData } from '../utils';

/** 每个 Sheet 的数据结构 */
export interface ISheetData {
  tableData: TTableRow[];
  columns: IColumnConfig[];
}

/** Sheet 数据 Map 类型（sheetId -> ISheetData） */
export type TSheetDataMap = Map<string, ISheetData>;

/** Hook 参数 */
interface IUseSheetDataParams {
  /** 初始 sheets 列表 */
  initialSheets: ISheet[];
}

/** Hook 返回值 */
interface IUseSheetDataReturn {
  /** sheetId -> ISheetData 的 Map */
  sheetDataMap: Map<string, ISheetData>;
  /** 获取指定 sheet 的数据 */
  getSheetData: (sheetId: string) => ISheetData | undefined;
  /** 设置指定 sheet 的数据 */
  setSheetData: (sheetId: string, data: ISheetData) => void;
  /** 处理 sheets 变化（新增、删除、重命名、排序、创建副本） */
  handleSheetsChange: (currentSheets: ISheet[], newSheets: ISheet[]) => ISheet[];
  /** 重置所有数据 */
  resetSheetDataMap: () => void;
}

/**
 * Sheet 数据管理 Hook
 * 封装所有 sheet 数据的状态管理逻辑，包括副本创建时的数据复制
 */
export const useSheetData = ({
  initialSheets,
}: IUseSheetDataParams): IUseSheetDataReturn => {
  // 根据 initialSheets 生成初始数据（所有 Sheet 都使用空白数据）
  const generateInitialData = (): [string, ISheetData][] => initialSheets.map((sheet) => [
    sheet.id,
    generateBlankSheetData()
  ]);

  // 使用 useMap 管理每个 sheet 的数据（sheetId -> ISheetData）
  const [
    sheetDataMap,
    { set: setSheetData, get: getSheetData, remove: removeSheetData, reset: resetSheetDataMap }
  ] = useMap<string, ISheetData>(generateInitialData());

  /**
   * 复制源 sheet 的数据到新 sheet（内部方法）
   */
  const duplicateSheetData = useCallback((sourceSheetId: string, newSheetId: string) => {
    const sourceData = getSheetData(sourceSheetId);
    if (sourceData) {
      // 深拷贝源 sheet 的数据到新 sheet
      setSheetData(newSheetId, {
        tableData: sourceData.tableData.map(row => ({ ...row })),
        columns: sourceData.columns.map(col => ({ ...col })),
      });
    }
  }, [getSheetData, setSheetData]);

  /**
   * 处理 sheets 变化（新增、删除、重命名、排序、创建副本）
   * @param currentSheets 当前的 sheets 列表
   * @param newSheets 新的 sheets 列表（可能包含 sourceId 标记的副本）
   * @returns 清理后的 sheets 列表（移除 sourceId 字段）
   */
  const handleSheetsChange = useCallback((currentSheets: ISheet[], newSheets: ISheet[]): ISheet[] => {
    const newSheetIds = newSheets.map(s => s.id);
    const oldSheetIds = currentSheets.map(s => s.id);

    // 处理新增的 sheet
    newSheets.forEach(sheet => {
      if (!oldSheetIds.includes(sheet.id)) {
        // 检查是否是副本（有 sourceId 字段）
        if (sheet.sourceId) {
          // 创建副本：复制源 sheet 的数据
          duplicateSheetData(sheet.sourceId, sheet.id);
        } else {
          // 普通新增：初始化为完全空白（无预设列标题）
          setSheetData(sheet.id, generateBlankSheetData());
        }
      }
    });

    // 清理被删除的 sheet 数据
    oldSheetIds.forEach(id => {
      if (!newSheetIds.includes(id)) {
        removeSheetData(id);
      }
    });

    // 清除 sourceId 字段（仅用于识别副本，不需要持久化）
    const cleanedSheets = newSheets.map(sheet => {
      if (sheet.sourceId) {
        const { sourceId, ...rest } = sheet;
        return rest;
      }
      return sheet;
    });

    return cleanedSheets;
  }, [duplicateSheetData, setSheetData, removeSheetData, generateBlankSheetData]);

  return {
    sheetDataMap,
    getSheetData,
    setSheetData,
    handleSheetsChange,
    resetSheetDataMap,
  };
};

export default useSheetData;
